import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, Shield, MessageCircle } from 'lucide-react';
import { services } from '../mockData';
import UniversalPopupForm from './UniversalPopupForm';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChatClick = () => {
    if (window.LiveChatWidget) {
      window.LiveChatWidget.call('maximize');
    } else {
      console.log('Zendesk widget not loaded yet');
      alert('Chat is loading... Please try again in a moment.');
    }
  };

  const isLandingPage = location.pathname === '/lp';
  if (isLandingPage) return null;

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white border-b border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img 
                src="/assets/img/brand/logo-dark.png"
                alt="Trademark Flare Logo"
                className="h-12 w-auto transform group-hover:scale-105 transition-transform duration-200"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-brand-600 font-medium transition-colors">
                Home
              </Link>
              <Link to="/about" className="text-gray-700 hover:text-brand-600 font-medium transition-colors">
                About Us
              </Link>
              
              {/* Services Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}
              >
                <button className="flex items-center text-gray-700 hover:text-brand-600 font-medium transition-colors">
                  Services
                  <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isServicesOpen && (
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
                    <div className="p-2">
                      {services.map((service) => (
                        <Link
                          key={service.id}
                          to={service.link}
                          className="block px-4 py-3 hover:bg-brand-50 rounded-lg transition-colors group"
                        >
                          <div className="font-semibold text-gray-900 group-hover:text-brand-600 mb-1">
                            {service.title}
                          </div>
                          <div className="text-sm text-gray-600">
                            {service.shortDesc}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link to="/contact" className="text-gray-700 hover:text-brand-600 font-medium transition-colors">
                Contact Us
              </Link>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <button
                onClick={() => setIsPopupOpen(true)}
                className="bg-gradient-to-r from-brand-600 to-brand-700 text-white font-semibold px-6 py-2.5 rounded-lg hover:from-brand-700 hover:to-brand-800 transform hover:scale-105 transition-all duration-200 shadow-md"
              >
                Protect Your Brand
              </button>
              <button
                onClick={handleChatClick}
                className="flex items-center space-x-2 border-2 border-brand-600 text-brand-600 font-semibold px-6 py-2.5 rounded-lg hover:bg-brand-50 transition-all duration-200"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Chat Now</span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="lg:hidden border-t border-gray-200 py-4 space-y-2">
              <Link to="/" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">
                Home
              </Link>
              <Link to="/about" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">
                About Us
              </Link>
              <div className="px-4 py-2">
                <div className="font-semibold text-gray-900 mb-2">Services</div>
                <div className="pl-4 space-y-1">
                  {services.map((service) => (
                    <Link
                      key={service.id}
                      to={service.link}
                      className="block py-2 text-sm text-gray-600 hover:text-brand-600"
                    >
                      {service.title}
                    </Link>
                  ))}
                </div>
              </div>
              <Link to="/contact" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">
                Contact Us
              </Link>
              <div className="px-4 pt-4 space-y-3 border-t border-gray-200">
                <button
                  onClick={() => {
                    setIsPopupOpen(true);
                    setIsMenuOpen(false);
                  }}
                  className="w-full bg-gradient-to-r from-brand-600 to-brand-700 text-white font-semibold py-2.5 rounded-lg"
                >
                  Protect Your Brand
                </button>
                <button
                  onClick={handleChatClick}
                  className="w-full flex items-center justify-center space-x-2 border-2 border-brand-600 text-brand-600 font-semibold py-2.5 rounded-lg"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Chat Now</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      <UniversalPopupForm isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default Header;
